import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Typography, Select, Alert, Button, Space, Divider, Table, Tag, Input, DatePicker, Progress, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useAppSelector } from '../../../Modules/Presentation/hooks';
import { selectAccounts } from '../../../Modules/Account/Presentation/selectors';
import { selectUser } from '../../../Modules/User/Presentation/selectors';
import type { CreateSlotDTO } from '../../../Modules/Slot/Model/Slot';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import styled from 'styled-components';

const { Paragraph, Text } = Typography;
const { Option } = Select;

interface Props {
  open: boolean;
  file: File | null;
  onClose: () => void;
  onSubmit: (request: { count: number; slots: CreateSlotDTO[] }) => Promise<void> | void;
}

type RawRow = Record<string, any>;

// 'count' 필드를 추가하기 위해 CreateSlotDTO에 없는 가상의 필드를 포함한 타입 정의
type ExtendedField = keyof Omit<CreateSlotDTO, 'sellerId'> | 'count';

const headerMap: Record<string, string[]> = {
  slot_number: ['slot_number', '슬롯번호', '슬롯 번호', '슬롯'],
  main_keyword: ['main_keyword', '메인키워드', '메인 키워드'],
  word_keyword: [], // 작업 키워드는 UI에서 제거되었으므로 매핑하지 않음
  mid_single: ['mid_single', '단일MID', '단일 MID'],
  mid_compare: ['mid_compare', '가격비교MID', '가격비교 MID', '원부MID', '원부 MID'],
  product_url: ['product_url', '상품URL', '상품 URL'],
  compare_url: ['compare_url', '가격비교URL', '가격비교 URL', '가격비교', '가격 비교', '가격비교링크'],
  memo: ['memo', '메모'],
  start_date: ['start_date', '시작일', 'start', 'Start'],
  end_date: ['end_date', '종료일', 'end', 'End'],
  status: ['status', '상태'],
  manager: ['manager', '매니저', '담당'],
  count: ['count', '수량', '개수', '갯수'],
};


const SELLER_HEADER = '셀러(Id)';

const ExcelImportModal: React.FC<Props> = ({ open, file, onClose, onSubmit }) => {
  const accounts = useAppSelector(selectAccounts);
  const currentUser = useAppSelector(selectUser);
  const isAdmin = useMemo(() => currentUser?.grade === 'ADMIN', [currentUser]);

  const sellers = useMemo(
    () => accounts.filter(a => a.grade === 'SELLER').sort((a, b) => a.username.localeCompare(b.username)),
    [accounts]
  );

  const [rawRows, setRawRows] = useState<RawRow[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  
  const [parseError, setParseError] = useState<string | null>(null);
  type RowDraft = Partial<CreateSlotDTO> & { key: number };
  const [rows, setRows] = useState<RowDraft[]>([]);
  const [bulkSellerId, setBulkSellerId] = useState<number | undefined>(undefined);
  const [mapping, setMapping] = useState<Record<string, string | null>>({
    slot_number: null,
    main_keyword: null,
    word_keyword: null,
    mid_single: null,
    mid_compare: null,
    product_url: null,
    compare_url: null,
    memo: null,
    start_date: null,
    end_date: null,
    status: null,
    manager: null,
    count: null,
  });

  // 반응형: 모바일 여부
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 489);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!open || !file) return;
    setParseError(null);
    setIsParsing(true);
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<RawRow>(ws, { defval: '' });
        setRawRows(rows);
      } catch (err) {
        setParseError('엑셀 파일을 읽는 중 오류가 발생했습니다.');
        setIsParsing(false);
      }
    };
    reader.onerror = () => {
      setParseError('엑셀 파일을 읽을 수 없습니다.');
      setIsParsing(false);
    };
    reader.readAsArrayBuffer(file);
  }, [open, file]);

  const detectedHeaders = useMemo(() => {
    const set = new Set<string>();
    rawRows.forEach(r => Object.keys(r).forEach(k => set.add(String(k))));
    return Array.from(set);
  }, [rawRows]);

  const resolveSellerId = useCallback((value: any): number | undefined => {
    if (value == null || value === '') return undefined;

    // 숫자면 그대로 id로 처리
    if (typeof value === 'number' && Number.isFinite(value)) return Number(value);

    const raw = String(value).trim();
    if (!raw) return undefined;

    // "123" 형태면 id로 처리
    if (/^\d+$/.test(raw)) return Number(raw);

    // "username (name)" / "username" 모두 지원
    const usernameCandidate = raw.split('(')[0].trim().split(/\s+/)[0].trim();
    const lower = usernameCandidate.toLowerCase();
    const matched = sellers.find(s => s.username.toLowerCase() === lower);
    return matched?.id;
  }, [sellers]);

  useEffect(() => {
    if (detectedHeaders.length === 0) return;
    const norm = (s: string) => s.replace(/\s+/g, '').toLowerCase();
    const next: Record<string, string | null> = { ...mapping } as any;
    let changed = false;
    (Object.keys(headerMap) as ExtendedField[]).forEach(field => {
      if (next[field]) return;
      const aliases = headerMap[field].map(norm);
      const found = detectedHeaders.find(h => aliases.includes(norm(h)) || aliases.some(a => norm(h).includes(a)));
      if (found) {
        next[field] = found;
        changed = true;
      }
    });
    if (changed) setMapping(next);
  }, [detectedHeaders, mapping]);

  // 엑셀 시리얼/문자 날짜를 YYYY-MM-DD 로 정규화
  const toDateString = (value: any): string => {
    if (value == null || value === '') return '';
    if (typeof value === 'number') {
      const date = new Date(Math.round((value - 25569) * 86400 * 1000));
      return dayjs(date).format('YYYY-MM-DD');
    }
    const d = dayjs(value);
    return d.isValid() ? d.format('YYYY-MM-DD') : String(value);
  };

  // 초기 rows 채우기 (자동 매핑만 사용, UI는 테이블에서 직접 수정)
  useEffect(() => {
    const norm = (s: any) => String(s ?? '').trim();
    const initial: RowDraft[] = [];
    let keyCounter = 0;

    rawRows.forEach((r, idx) => {
      const pick = (field: string): any => {
        const header = mapping[field];
        if (!header) return '';
        return r[header];
      };
      // 엑셀 가이드: '셀러(Id)' 컬럼에 username(또는 숫자 id)이 들어온다고 가정
      // (혹시 예전 파일로 '셀러(id)'가 들어오는 경우도 있어 최소한으로만 fallback)
      const sellerRaw = r[SELLER_HEADER] ?? r['셀러(id)'];
      const startRaw = pick('start_date');
      const endRaw = pick('end_date');
      const countRaw = pick('count');
      const count = parseInt(countRaw);
      const actualCount = isNaN(count) || count <= 0 ? 1 : count;

      const baseRow = {
        // 엑셀의 "셀러(id)" 컬럼: 숫자면 id, 문자면 username으로 간주하고 accounts에서 id로 매칭
        sellerId: resolveSellerId(sellerRaw),
        manager: (pick('manager') as any) ?? null,
        slot_number: norm(pick('slot_number')),
        main_keyword: norm(pick('main_keyword')),
        word_keyword: '-',
        mid_single: norm(pick('mid_single')),
        mid_compare: norm(pick('mid_compare')),
        product_url: norm(pick('product_url')),
        compare_url: norm(pick('compare_url')),
        memo: norm(pick('memo')) || undefined,
        start_date: toDateString(startRaw),
        end_date: toDateString(endRaw),
        status: (norm(pick('status')) as CreateSlotDTO['status']) || '정상',
      };

      for (let i = 0; i < actualCount; i++) {
        initial.push({ ...baseRow, key: keyCounter++ });
      }
    });
    setRows(initial);
    setBulkSellerId(undefined);
    setIsParsing(false);
  }, [rawRows, mapping, resolveSellerId]);

  const minStartDate = useMemo(() => {
    if (isAdmin) return dayjs().startOf('day');
    return dayjs().add(1, 'day').startOf('day');
  }, [isAdmin]);

  const getRowErrors = useCallback((r: RowDraft): string[] => {
    const errs: string[] = [];
    if (!r.sellerId) errs.push('셀러 미선택');
    if (!r.start_date) errs.push('시작일 없음');
    else if (dayjs(r.start_date).startOf('day').isBefore(minStartDate)) {
      errs.push(isAdmin ? '시작일은 오늘 이후' : '시작일은 내일 이후');
    }
    if (!r.end_date) errs.push('종료일 없음');
    else {
      const e = dayjs(r.end_date).startOf('day');
      // start_date가 있으면 "종료일 >= 시작일", 없으면 "종료일 >= 최소시작일"
      if (r.start_date) {
        const s = dayjs(r.start_date).startOf('day');
        if (e.isBefore(s)) errs.push('종료일은 시작일과 같거나 이후');
      } else {
        if (e.isBefore(minStartDate)) {
          errs.push(isAdmin ? '종료일은 오늘 이후' : '종료일은 내일 이후');
        }
      }
    }

    const urlRegex = /^(http|https):\/\/[^ "]+$/;
    if (r.product_url && !urlRegex.test(r.product_url)) {
      errs.push('상품 URL 형식이 올바르지 않습니다.');
    }
    if (r.compare_url && !urlRegex.test(r.compare_url)) {
      errs.push('가격비교 URL 형식이 올바르지 않습니다.');
    }

    return errs;
  }, [minStartDate, isAdmin]);

  const isRowValid = useCallback((r: RowDraft) => getRowErrors(r).length === 0, [getRowErrors]);

  const { mapped, invalidCount } = useMemo(() => {
    const valid: CreateSlotDTO[] = [];
    rows.forEach((r: RowDraft) => {
      if (!isRowValid(r)) return;
      
      valid.push({
        sellerId: r.sellerId!,
        manager: r.manager ?? null,
        slot_number: r.slot_number || '',
        main_keyword: r.main_keyword || '',
        word_keyword: '-',
        mid_single: r.mid_single || '',
        mid_compare: r.mid_compare || '',
        product_url: r.product_url || '',
        compare_url: r.compare_url || '',
        memo: r.memo,
        start_date: r.start_date!,
        end_date: r.end_date!,
        status: r.status || '정상',
      });
    });
    return { mapped: valid, invalidCount: rows.length - (rows.filter(isRowValid).length) };
  }, [rows, isRowValid]);

  const canSubmit = useMemo(() => rows.length > 0 && rows.every(isRowValid), [rows, isRowValid]);

  const updateRow = useCallback((key: number, field: keyof RowDraft, value: any) => {
    setRows(prev => prev.map(r => r.key === key ? { ...r, [field]: value } : r));
  }, []);

  const Metrics = styled(Space)`
    width: 100%;
  `;

  const FullWidth = styled(Space)`
    width: 100%;
  `;

  return (
    <Modal
      title="엑셀 업로드 - 행별 편집 및 등록"
      open={open}
      onCancel={onClose}
      width={isMobile ? undefined : 1100}
      bodyStyle={isMobile ? { paddingBottom: 0 } : { paddingBottom: 0, maxHeight: '80vh', overflowY: 'auto' }}
      footer={[
        <Button key="cancel" onClick={onClose}>취소</Button>,
        <Button key="submit" type="primary" disabled={!canSubmit} onClick={() => {
          const slots: CreateSlotDTO[] = mapped;
          onSubmit({ count: 1, slots });
        }}>등록</Button>,
      ]}
    >
      <FullWidth direction="vertical" size="middle">
        <Paragraph>
          엑셀을 읽었습니다. 각 행에서 셀러 및 값을 직접 수정하세요. (상단에서 셀러를 한 번에 적용할 수도 있습니다.) 상태는 수정할 수 없습니다.
        </Paragraph>

        {isParsing && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" tip="엑셀 파일을 분석 중입니다..." />
          </div>
        )}

        {!isParsing && parseError && <Alert type="error" message={parseError} />}

        {!isParsing && (
          <>
            <Metrics size="small">
              <Space size="small"><Text>총 행 수(슬롯 수):</Text><Text strong>{rows.length}</Text></Space>
              <Space size="small"><Text>등록 가능:</Text><Text strong>{mapped.length}</Text></Space>
              {invalidCount > 0 && (
                <Space size="small"><Text>등록 불가 행:</Text><Text type="danger">{invalidCount}</Text></Space>
              )}
            </Metrics>

            <Space wrap size="small" style={{ width: '100%' }}>
              <Text strong>셀러 일괄 적용:</Text>
              <Select
                value={bulkSellerId}
                style={{ width: 260 }}
                placeholder="셀러 선택 (전체 행에 적용)"
                allowClear
                showSearch
                optionFilterProp="label"
                optionLabelProp="label"
                onChange={(val?: number) => {
                  setBulkSellerId(val);
                  setRows(prev => prev.map(r => ({ ...r, sellerId: val })));
                }}
              >
                {sellers.map(s => (
                  <Option key={s.id} value={s.id} label={`${s.username} (${s.name})`}>
                    {s.username} ({s.name})
                  </Option>
                ))}
              </Select>
              <Button
                  onClick={() => {
                    setBulkSellerId(undefined);
                    setRows(prev => prev.map(r => ({ ...r, sellerId: undefined })));
                  }}
                >
                  전체 셀러 비우기
                </Button>
            </Space>

            <Divider style={{ margin: '8px 0' }} />

            <Table
              size="small"
              pagination={false}
              dataSource={rows}
              scroll={{ x: 1800, y: 500 }}
              virtual
              columns={[
                {
                  title: '셀러',
                  dataIndex: 'sellerId',
                  width: 220,
                  render: (_: unknown, r: RowDraft) => (
                    <div style={{ padding: '8px 0' }}>
                      <Select
                          value={r.sellerId as number | undefined}
                          style={{ width: '100%' }}
                          placeholder="셀러 선택"
                          showSearch
                          optionFilterProp="label"
                          optionLabelProp="label"
                          status={!r.sellerId ? 'error' : undefined}
                          onChange={(val: number) => updateRow(r.key, 'sellerId', val)}
                        >
                          {sellers.map(s => (
                            <Option key={s.id} value={s.id} label={`${s.username} (${s.name})`}>
                              {s.username} ({s.name})
                            </Option>
                          ))}
                        </Select>
                    </div>
                  )
                },
                { title: '메인 키워드', dataIndex: 'main_keyword', width: 200, render: (v: any, r: any) => (
                  <div style={{ padding: '8px 0' }}>
                    <Input value={v as string} onChange={e => updateRow(r.key, 'main_keyword', e.target.value)} />
                  </div>
                ) },
                { title: '단일 MID', dataIndex: 'mid_single', width: 160, render: (v: any, r: any) => (
                  <div style={{ padding: '8px 0' }}>
                    <Input value={v as string} onChange={e => updateRow(r.key, 'mid_single', e.target.value)} />
                  </div>
                ) },
                { title: '가격비교 MID', dataIndex: 'mid_compare', width: 180, render: (v: any, r: any) => (
                  <div style={{ padding: '8px 0' }}>
                    <Input value={v as string} onChange={e => updateRow(r.key, 'mid_compare', e.target.value)} />
                  </div>
                ) },
                { title: '상품 URL', dataIndex: 'product_url', width: 260, render: (v: any, r: any) => {
                  const urlRegex = /^(http|https):\/\/[^ "]+$/;
                  const isInvalid = v && !urlRegex.test(v);
                  return (
                    <div style={{ padding: '8px 0' }}>
                      <Input 
                          value={v as string} 
                          status={isInvalid ? 'error' : undefined}
                          onChange={e => updateRow(r.key, 'product_url', e.target.value)} 
                        />
                    </div>
                  );
                } },
                { title: '가격비교 URL', dataIndex: 'compare_url', width: 260, render: (v: any, r: any) => {
                  const urlRegex = /^(http|https):\/\/[^ "]+$/;
                  const isInvalid = v && !urlRegex.test(v);
                  return (
                    <div style={{ padding: '8px 0' }}>
                      <Input 
                          value={v as string} 
                          status={isInvalid ? 'error' : undefined}
                          onChange={e => updateRow(r.key, 'compare_url', e.target.value)} 
                        />
                    </div>
                  );
                } },
                { title: '시작일', dataIndex: 'start_date', width: 150, render: (v: string | undefined, r: RowDraft) => {
                  const invalid = !v || dayjs(v).startOf('day').isBefore(minStartDate);
                  return (
                    <div style={{ padding: '8px 0' }}>
                      <DatePicker
                          value={v ? dayjs(v as string) : undefined}
                          disabledDate={(current) => !!current && current.startOf('day').isBefore(minStartDate)}
                          status={invalid ? 'error' : undefined}
                          onChange={d => updateRow(r.key, 'start_date', d ? d.format('YYYY-MM-DD') : '')}
                        />
                    </div>
                  );
                } },
                { title: '종료일', dataIndex: 'end_date', width: 150, render: (v: string | undefined, r: RowDraft) => {
                  const invalid = !v || (r.start_date
                    ? dayjs(v).startOf('day').isBefore(dayjs(r.start_date).startOf('day'))
                    : dayjs(v).startOf('day').isBefore(minStartDate));
                  return (
                    <div style={{ padding: '8px 0' }}>
                      <DatePicker
                          value={v ? dayjs(v as string) : undefined}
                          disabledDate={(current) => {
                            if (!current) return false;
                            const c = current.startOf('day');
                            const min = r.start_date ? dayjs(r.start_date).startOf('day') : minStartDate;
                            return c.isBefore(min);
                          }}
                          status={invalid ? 'error' : undefined}
                          onChange={d => updateRow(r.key, 'end_date', d ? d.format('YYYY-MM-DD') : '')}
                        />
                    </div>
                  );
                } },
                { title: '메모', dataIndex: 'memo', width: 200, render: (v: any, r: any) => (
                  <div style={{ padding: '8px 0' }}>
                    <Input value={v as string} onChange={e => updateRow(r.key, 'memo', e.target.value)} />
                  </div>
                ) },
                { title: '상태', dataIndex: 'status', render: (v: string | undefined) => (
                  <Tag>{v || '정상'}</Tag>
                ) },
              ]}
              rowClassName={(r: RowDraft) => {
                const startInvalid = !r.start_date || dayjs(r.start_date).startOf('day').isBefore(minStartDate);
                return (!r.sellerId || startInvalid || !r.end_date) ? 'row-invalid' : '';
              }}
            />
          </>
        )}
      </FullWidth>
    </Modal>
  );
};

export default ExcelImportModal;


